
import { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Edit, Save } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface SystemPrompt {
  id: string;
  chatbotId: string;
  chatbotName: string;
  content: string;
}

interface SystemPromptsProps {
  systemPrompts: SystemPrompt[];
  onPromptUpdated: (chatbotId: string, content: string) => void;
}

const SystemPrompts = ({ systemPrompts, onPromptUpdated }: SystemPromptsProps) => {
  const { toast } = useToast();
  const [selectedPrompt, setSelectedPrompt] = useState<SystemPrompt | null>(null);
  const [promptContent, setPromptContent] = useState('');
  
  // Edit a system prompt
  const handleEditPrompt = (prompt: SystemPrompt) => {
    setSelectedPrompt(prompt);
    setPromptContent(prompt.content);
  };
  
  // Save edited system prompt
  const handleSavePrompt = async () => {
    if (selectedPrompt) {
      try {
        const { error } = await supabase
          .from('system_prompts')
          .update({ content: promptContent })
          .eq('agent_id', selectedPrompt.chatbotId);
        
        if (error) throw error;
        
        // Notify parent component
        onPromptUpdated(selectedPrompt.chatbotId, promptContent);
        
        setSelectedPrompt(null);
        toast({
          title: "System Prompt Updated",
          description: "The changes have been saved"
        });
      } catch (error) {
        console.error('Error updating prompt:', error);
        toast({
          title: "Error",
          description: "Failed to update system prompt",
          variant: "destructive"
        });
      }
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>System Prompts</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            System prompts define the behavior and knowledge of each AI persona.
          </p>
          
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Persona</TableHead>
                <TableHead>System Prompt</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {systemPrompts.length > 0 ? (
                systemPrompts.map(prompt => (
                  <TableRow key={prompt.id}>
                    <TableCell className="font-medium">{prompt.chatbotName}</TableCell>
                    <TableCell className="max-w-md">
                      <div className="truncate">{prompt.content}</div>
                    </TableCell>
                    <TableCell>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleEditPrompt(prompt)}
                          >
                            <Edit size={14} />
                            <span className="sr-only">Edit</span>
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-xl">
                          <DialogHeader>
                            <DialogTitle>Edit System Prompt for {prompt.chatbotName}</DialogTitle>
                          </DialogHeader>
                          <div className="py-4">
                            <Textarea 
                              className="min-h-[200px] font-mono text-sm"
                              value={promptContent}
                              onChange={(e) => setPromptContent(e.target.value)}
                              placeholder="Enter system prompt instructions..."
                            />
                            <div className="text-xs text-muted-foreground mt-2">
                              The system prompt sets the persona, behavior, and knowledge boundaries 
                              for the AI character. Be specific about the time period, personality traits, 
                              and tone.
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <Button onClick={handleSavePrompt}>
                              <Save size={16} className="mr-2" />
                              Save Changes
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-4 text-muted-foreground">
                    No system prompts found. Add some personas first.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default SystemPrompts;
